import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Link, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Fund } from '../api/useFunds';
import { CashbackModel } from '../utils/cashbackConstants';
import { CashbackComparisonChart } from './ChartSection/CashbackComparisonChart';
import { calculateCashback } from './cashbackCalculatorUtil';

const Container = styled('div')(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(3, 0, 4),
}));

const ShowChartButton = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  padding: theme.spacing(0, 4),

  '&:hover': {
    textDecoration: 'none',
  },

  '& svg': {
    width: '24px',
    height: '24px',
  },
}));

const ChartContainer = styled('div')(({ theme }) => ({
  height: '280px',
  width: '468px',

  padding: theme.spacing(0, 4),
}));

const ChartOuterContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'showChart',
})<{ showChart: boolean }>(({ showChart }) => ({
  width: '100%',
  overflowY: 'hidden',
  overflowX: showChart ? 'auto' : 'hidden',
  maxHeight: showChart ? '468px' : '0',
  transition: 'max-height 0.3s',
}));

type CashbackDisclaimerProps = {
  fundsMap: Map<Fund, number>;
  bic: Bic;
  model?: CashbackModel;
  onChartBarClick: (bic: Bic) => void;
};

export const CashbackDisclaimer = ({
  fundsMap,
  bic,
  model,
  onChartBarClick,
}: CashbackDisclaimerProps) => {
  const { t } = useTranslation();
  const [showChart, setShowChart] = useState(false);
  const theme = useTheme();
  const isLessMd = useMediaQuery(theme.breakpoints.down('md'));
  const cashbackAdvantage = calculateCashback(fundsMap, bic, model);
  const showChartButton = cashbackAdvantage.cashback > 0;

  return (
    <Container>
      {isLessMd && showChartButton && (
        <ShowChartButton onClick={() => setShowChart(!showChart)}>
          <Typography variant="body2" sx={{ marginRight: theme.spacing(1) }}>
            {t('cashbackCalculator.detailed.cashbackDisclaimer.showChart')}
          </Typography>
          <ExpandMoreIcon
            color="inherit"
            sx={{
              transform: showChart ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
              transformOrigin: 'center',
            }}
          />
        </ShowChartButton>
      )}
      {isLessMd && (
        <ChartOuterContainer showChart={showChart}>
          <Typography
            variant="h6"
            sx={{
              fontSize: '16px',
              lineHeight: '24px',
              padding: theme.spacing(0, 4),
              marginBottom: theme.spacing(3),
            }}
          >
            {t('cashbackCalculator.detailed.calculator.chartHeading')}
          </Typography>
          <ChartContainer>
            <CashbackComparisonChart fundsMap={fundsMap} bic={bic} onClick={onChartBarClick} />
          </ChartContainer>
        </ChartOuterContainer>
      )}
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{
          padding: theme.spacing(0, isLessMd ? 4 : 5),
        }}
      >
        {t('cashbackCalculator.detailed.cashbackDisclaimer.label')}
      </Typography>
    </Container>
  );
};
